import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { createSyncJob, CreateSyncJobParams } from './create-sync-job';
import {
  clearSyncJob,
  getSyncJob,
} from './sync-job-store';
import {
  continueSyncJob,
  isSyncRunnerActive,
  pauseSyncJob,
  startSyncJob,
} from './sync-job-runner';
import {
  computeSyncProgress,
  isActiveSyncPhase,
  isResumableSyncPhase,
  SyncJob,
  SyncJobProgress,
} from './types';

export function useSyncJob() {
  const queryClient = useQueryClient();
  const [job, setJob] = useState<SyncJob | undefined>();
  const [isRunning, setIsRunning] = useState(false);
  const pauseRequestedRef = { current: false };

  const refreshJob = useCallback(async () => {
    const stored = await getSyncJob();
    setJob(stored);
    return stored;
  }, []);

  useEffect(() => {
    refreshJob();
    const interval = setInterval(() => {
      if (isSyncRunnerActive() || isRunning) {
        refreshJob();
      }
    }, 500);
    return () => clearInterval(interval);
  }, [refreshJob, isRunning]);

  const progress: SyncJobProgress | undefined = job
    ? computeSyncProgress(job)
    : undefined;

  const runJob = useCallback(
    async (params: CreateSyncJobParams) => {
      pauseRequestedRef.current = false;
      const newJob = createSyncJob(params);
      setIsRunning(true);
      try {
        const result = await startSyncJob(queryClient, newJob, {
          onJobUpdate: setJob,
          shouldAbort: () => pauseRequestedRef.current,
        });
        setJob(result);
        return result;
      } finally {
        setIsRunning(false);
        pauseRequestedRef.current = false;
      }
    },
    [queryClient]
  );

  const resumeJob = useCallback(async () => {
    pauseRequestedRef.current = false;
    setIsRunning(true);
    try {
      const result = await continueSyncJob(queryClient, {
        onJobUpdate: setJob,
        shouldAbort: () => pauseRequestedRef.current,
      });
      if (result) {
        setJob(result);
      }
      return result;
    } finally {
      setIsRunning(false);
      pauseRequestedRef.current = false;
    }
  }, [queryClient]);

  const pauseJob = useCallback(async () => {
    pauseRequestedRef.current = true;
    const paused = await pauseSyncJob();
    if (paused) {
      setJob(paused);
    }
    return paused;
  }, []);

  const cancelJob = useCallback(async () => {
    pauseRequestedRef.current = true;
    await clearSyncJob();
    setJob(undefined);
  }, []);

  return {
    job,
    progress,
    isRunning: isRunning || isSyncRunnerActive(),
    isActive: job ? isActiveSyncPhase(job.status) : false,
    isResumable: job ? isResumableSyncPhase(job.status) : false,
    refreshJob,
    runJob,
    resumeJob,
    pauseJob,
    cancelJob,
  };
}
